// src/services/bunnyPlayerBridge.js
// Minimal Player.js client (the player.js spec by Embedly, which Bunny Stream's
// iframe embed implements) over postMessage. Subscribes to `timeupdate` from a
// Bunny embed iframe and reports { seconds, duration }.
//
// Used to bring PROGRAM (Bunny) videos to auto-completion parity with class
// recordings when they render as a cross-origin iframe (no native timeupdate).
// See docs/decisions/adr-completions-event-log.md §2.7.
//
// GRACEFUL DEGRADATION (load-bearing): if the iframe doesn't speak Player.js
// (blocked, older embed, API change), onTimeUpdate simply never fires and the
// caller falls back to manual completion — never an error, never a throw. This
// mirrors the safety posture of serverClock / castSession.
//
// NOTE: this depends on Bunny honoring the Player.js postMessage handshake at
// runtime, which can only be confirmed on-device. Treat program auto-complete as
// requiring a live verification pass (like CAST-001).

export function subscribeBunnyProgress(iframe, onTimeUpdate) {
    if (!iframe || !iframe.contentWindow || typeof onTimeUpdate !== 'function') return () => {};
    let active = true;

    const post = (method, value) => {
        try {
            iframe.contentWindow.postMessage(JSON.stringify({
                context: 'player.js',
                version: '0.0.1',
                method,
                value,
            }), '*');
        } catch (_) { /* cross-origin not ready / blocked — ignore */ }
    };

    const onMessage = (e) => {
        if (!active) return;
        // Only trust messages coming from THIS iframe's window.
        if (e.source !== iframe.contentWindow) return;
        let data = e.data;
        if (typeof data === 'string') {
            try { data = JSON.parse(data); } catch (_) { return; }
        }
        if (!data || data.context !== 'player.js') return;
        if (data.event === 'ready') {
            // Register once the player announces readiness.
            post('addEventListener', 'timeupdate');
            return;
        }
        if (data.event === 'timeupdate' && data.value) {
            const seconds = Number(data.value.seconds);
            const duration = Number(data.value.duration);
            if (Number.isFinite(seconds) && Number.isFinite(duration)) {
                onTimeUpdate(seconds, duration);
            }
        }
    };

    window.addEventListener('message', onMessage);
    // Belt and suspenders: the player may already be ready before we attach, so
    // proactively subscribe in addition to reacting to the 'ready' event.
    post('addEventListener', 'ready');
    post('addEventListener', 'timeupdate');

    return () => {
        active = false;
        try { post('removeEventListener', 'timeupdate'); } catch (_) { /* ignore */ }
        window.removeEventListener('message', onMessage);
    };
}
